import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2, Mail } from "lucide-react";
import { AnimatedWords } from "@/components/motion";
import { submitContact } from "@/lib/api";

const inputCls =
  "w-full border border-white/15 bg-[#101010] px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-colors duration-200 focus:border-teal focus:ring-1 focus:ring-teal";

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-2">
    <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">{label}</span>
    {children}
  </label>
);

export default function Contact() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your first name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ first_name: "", last_name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#0A0A0A] py-24 md:py-32">
      <div className="mx-auto grid max-w-[1400px] gap-16 px-6 md:px-12 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <div className="mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-teal" />
            <span className="font-condensed text-base tracking-[0.35em] text-teal">GET IN TOUCH</span>
          </div>
          <h2 className="font-display text-5xl leading-[0.88] uppercase sm:text-6xl md:text-7xl">
            <AnimatedWords text="Questions?" />
            <br />
            <span className="accent-teal">
              <AnimatedWords text="We're here" delay={0.15} />
            </span>
          </h2>
          <p className="mt-6 max-w-sm text-base leading-relaxed text-zinc-400 md:text-lg">
            Have questions about Trainova? Drop us a message and the team will get back to you.
          </p>
          <a
            href="mailto:support@trainovaapp.com"
            data-testid="contact-email-link"
            className="mt-8 inline-flex items-center gap-3 text-zinc-300 transition-colors duration-200 hover:text-teal"
          >
            <Mail size={18} /> support@trainovaapp.com
          </a>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col gap-6 border border-white/10 bg-[#0d0d0d] p-8 md:p-10"
        >
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="First name *">
              <input data-testid="contact-firstname-input" value={form.first_name} onChange={set("first_name")} placeholder="Jordan" className={inputCls} />
            </Field>
            <Field label="Last name">
              <input data-testid="contact-lastname-input" value={form.last_name} onChange={set("last_name")} placeholder="Rivera" className={inputCls} />
            </Field>
          </div>
          <Field label="Email *">
            <input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
          </Field>
          <Field label="Message *">
            <textarea data-testid="contact-message-input" value={form.message} onChange={set("message")} rows={5} placeholder="Write a message..." className={inputCls + " resize-none"} />
          </Field>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-btn"
            className="group inline-flex items-center justify-center gap-3 self-start bg-teal px-8 py-4 font-bold uppercase tracking-wide text-black transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Submit"}
            {!loading && <Send size={18} className="transition-transform duration-200 group-hover:translate-x-1" />}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
